import type {
  Content,
  ContentHref,
  ContentRelation,
  PersonHref,
  Proposal,
} from '@generalTypes/apiTypes';
import type { Person } from '@generalTypes/personApiTypes';
import { formatDate, isNotNull } from '@newStore/genericHelpers';
import { getAllDescendantHrefs } from '../documentUIHelpers';
import type { Mode } from '../documentUIState';
import type { DiffMessage, ProposalMetaInfo, ProposalType } from '../documentUITypes';

/**
 * Gathers all proposals for any descendent (including deleted descendants) not mentioned in excludeHrefs or below that.
 */
export const getAllDescendantProposals = (
  proposalsMap: Record<ContentHref, Proposal>,
  toRelationsMap: Record<string, Array<ContentRelation>>,
  href: ContentHref,
  excludeHrefs?: ContentHref[],
): Proposal[] => {
  const descendantHrefs = getAllDescendantHrefs(toRelationsMap, href, new Set(excludeHrefs));
  return descendantHrefs.map((dHref) => proposalsMap[dHref]).filter(isNotNull);
};

export const mergeProposals = (proposals: Proposal[]): Proposal | null => {
  if (!proposals.length) {
    return null;
  }
  return proposals.reduce((mergedProposal, proposal) => ({
    ...mergedProposal,
    $$meta: {
      ...mergedProposal.$$meta,
      modified:
        proposal.status === 'SUBMITTED_FOR_REVIEW' &&
        proposal.$$meta.modified > mergedProposal.$$meta.modified
          ? proposal.$$meta.modified
          : mergedProposal.$$meta.modified,
    },
    creators: [...new Set([...mergedProposal.creators, ...proposal.creators])],
    status:
      mergedProposal.status === 'SUBMITTED_FOR_REVIEW' || proposal.status === 'SUBMITTED_FOR_REVIEW'
        ? 'SUBMITTED_FOR_REVIEW'
        : 'IN_PROGRESS',
    listOfRequestedChanges: [
      ...mergedProposal.listOfRequestedChanges,
      ...proposal.listOfRequestedChanges,
    ],
  }));
};

/* const mergeDiffText = (elems: Array<DiffTextInput>): Array<DiffMessage> =>
  elems.reduce((allDiffMsgs, calcInput) => {
    return [...allDiffMsgs, ...getDiffMessages(calcInput)];
  }, [] as Array<DiffMessage>); */

/**
 * returns the proposal and the diff text for the content node which is not the same as get the proposal for the content item
 * When a content item has hidden children, the children become part of the node
 * Therefore the proposal must integrate the proposals on the children as part of the proposal on this node
 */
export const getProposalForNode = (
  href: ContentHref,
  proposalsMap: Record<ContentHref, Proposal>,
  content: Content,
  hiddenDescendants: Content[],
) => {
  if (!hiddenDescendants.length) {
    return proposalsMap[href] || null;
  }
  // use case for node with hiddenDesendants they become part of the same node and so the proposal needs to be merged.
  const nodeProposals = [content, ...hiddenDescendants].flatMap((contentItem) => {
    const proposal = proposalsMap[contentItem.$$meta.permalink];
    return proposal || [];
  });
  const mergedProposal = mergeProposals(nodeProposals);
  return mergedProposal;
};

const getChildNodeProposalsText = (nbrOfChildProposals: number, mode: Mode): DiffMessage => {
  // the number could be a lie if there ar proposals of "hidden content items" which belong to their parent node but it's close enough
  if (mode !== 'EDIT') {
    return {
      message: `Er ${nbrOfChildProposals > 1 ? 'zijn' : 'is'} ${nbrOfChildProposals} suggestie${
        nbrOfChildProposals > 1 ? 's' : ''
      } voor de onderliggende inhoud`,
    };
  }
  return {
    message: `Er ${nbrOfChildProposals > 1 ? 'zijn' : 'is'} ${nbrOfChildProposals} wijziging${
      nbrOfChildProposals > 1 ? 'en' : ''
    } aan de onderliggende inhoud`,
  };
};

const getAuthorsDescription = (authors: Person[], userHref: string | null) =>
  authors
    .map((p) => (p.$$meta.permalink === userHref ? 'jezelf' : `${p.firstName} ${p.lastName}`))
    .join(', ')
    .replace(/,(?=[^,]+$)/, ' &');

export const transformProposal = (
  proposal: Proposal | null,
  personsMap: Record<string, Person>,
  userHref: PersonHref,
  nbOfChildProposals: number,
  type: ProposalType,
  mode: Mode,
): ProposalMetaInfo => {
  const authors =
    proposal === null
      ? [personsMap[userHref]]
      : proposal.creators.map((pHref) => personsMap[pHref]).filter((z) => z);
  return {
    href: proposal?.$$meta.permalink || null,
    status: proposal?.status || 'IN_PROGRESS',
    authors: authors.map((p) => ({
      href: p.$$meta.permalink,
      firstName: p.firstName,
      lastName: p.lastName,
    })),
    authorsDescription: getAuthorsDescription(authors, userHref),
    userIsAuthor: proposal ? proposal.creators.some((pHref) => pHref === userHref) : true,
    submittedOn:
      proposal?.status === 'SUBMITTED_FOR_REVIEW' ? formatDate(proposal.$$meta.modified) : null,
    childProposalsMessage:
      nbOfChildProposals > 0 ? getChildNodeProposalsText(nbOfChildProposals, mode) : null,
    type,
  };
};

export const wrapNodeTypeLabel = (strings, ...values) => {
  let str = '';

  values.forEach((value, index) => {
    if (index === 0) {
      str += `${strings[index]}<span class="node-type-label">${value}</span>`;
    } else {
      str += `${strings[index]}${value}`;
    }
  });

  str += strings[strings.length - 1];
  return str;
};
